// One reader for `user.selected_plan`, shared by the plan screen (Package.jsx),
// the admin roster (TableComponent.jsx, #90) and the admin client header
// (DashboardUserHeader.jsx, #91).
//
// `selected_plan` has two live shapes, both written by current server code:
//   - `purchase-subscription` stores the Stripe **Subscription**: `id` is a
//     `sub_…`, the price is under `items.data[0].price` (and the legacy `plan`),
//     and `status` is the subscription status
//   - `change-plan` stores the Stripe **Price**: `id` is the `price_…`, and
//     there is no subscription status on it
// Before payment the plan screen also writes the chosen PricePlan document
// (`{ _id, nickname, stripePriceId, amount }`). localStorage is user-editable,
// so every reader here tolerates garbage and never throws.

const LOOKUP_KEY_SUFFIX = /-[0-9a-f]{8}$/i; // `${nickname}-${randomBytes(4).hex}`
const LEGACY_LOOKUP_SUFFIX = /\s+price$/i; // seeded 'Essential price' / 'Platinum price'

const STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  paused: 'Paused',
};

function asObject(value) {
  return value && typeof value === 'object' ? value : null;
}

// The Stripe price id of the plan the user is on. The probe order matches the
// backend's scripts/deploy/reset-meeting-entitlements.js so a user's plan
// resolves the same way on both sides.
export function getSelectedPlanPriceId(selectedPlan) {
  return (
    selectedPlan?.plan?.id ||
    selectedPlan?.items?.data?.[0]?.price?.id ||
    selectedPlan?.id ||
    null
  );
}

export function getSelectedPlanPrice(selectedPlan) {
  const sp = asObject(selectedPlan);
  if (!sp) return null;
  const nested = asObject(sp.items?.data?.[0]?.price);
  if (nested) return nested;
  const legacyPlan = asObject(sp.plan);
  if (legacyPlan) return legacyPlan;
  if (sp.object === 'price' || (typeof sp.id === 'string' && sp.id.startsWith('price_'))) return sp;
  return null;
}

function labelFromLookupKey(lookupKey) {
  if (typeof lookupKey !== 'string') return null;
  const label = lookupKey.replace(LOOKUP_KEY_SUFFIX, '').replace(LEGACY_LOOKUP_SUFFIX, '').trim();
  return label || null;
}

function nonEmptyString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function getSelectedPlanName(selectedPlan) {
  const sp = asObject(selectedPlan);
  if (!sp) return '-';
  const price = getSelectedPlanPrice(sp);
  return (
    nonEmptyString(price?.nickname) ||
    nonEmptyString(sp.plan?.nickname) ||
    nonEmptyString(sp.nickname) ||
    labelFromLookupKey(price?.lookup_key) ||
    labelFromLookupKey(sp.lookup_key) ||
    '-'
  );
}

// A purchase-time snapshot of the Stripe subscription status. A price-shaped
// plan has no status, so it shows '-' rather than a guessed "Active" from the
// price's own `active` flag (#91).
export function getSelectedPlanStatusLabel(selectedPlan) {
  const status = asObject(selectedPlan)?.status;
  if (typeof status !== 'string' || !status.trim()) return '-';
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const words = status.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
